import { useCallback, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useClient } from 'urql';
import { fromPromise } from 'xstate5';

import { useNoticeBannersStackContext } from '@sg/garnish';

import { type SubmitOrderParams, type SubmitOrderResponse } from '@order/features-ordering';
import { useTelemetry } from '@order/Telemetry';

import { useNotificationType } from '../../../../integrations';
import {
  SubmitBagOrderWithPaymentMethodDocument,
  type SubmitBagOrderWithPaymentMethodMutation,
  type SubmitBagOrderWithPaymentMethodMutationVariables,
} from '../../GraphQL/BagSubmitOrder.generated';
import { getDeliveryParams } from '../../utils';
import { type useDefaultPaymentMethod } from '../useDefaultPaymentMethod';

// ────────────────────────────────────────────────────────────────────────────

/**
 * A hook for submitting an order.
 */
export const useSubmitOrder = (
  setDefaultPaymentMethod: ReturnType<typeof useDefaultPaymentMethod>['setDefaultPaymentMethod'],
) => {
  const { track } = useTelemetry();
  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const getNotificationType = useNotificationType();

  // ─── Mutations ────────────────────────────────────────────────────────────

  const submitOrderWithPaymentMethod = useSubmitOrderWithPaymentMethodMutation(); // prettier-ignore

  const submitOrder = useMemo(
    () =>
      fromPromise<SubmitOrderResponse, SubmitOrderParams>(async ({ input }) => {
        const {
          cart,
          paymentMethodOrIntentId = '',
          dropoffLocationId,
          wantedTime,
          selectedTip,
          shouldUseCredit,
          shouldUseFlex,
          shouldIncludeUtensils,
        } = input;

        if (!wantedTime) {
          addNoticeBanner({
            text: formatMessage(messages.missingWantedTime),
            palette: 'caution',
          });

          return {
            success: false,
            isTimeslotUnavailable: false,
            error: 'MissingWantedTime',
          };
        }

        const {
          orderId = '',
          orderChannel = 'pickup',
          deliveryOrderDetail,
          canTrackOrderStatus,
        } = cart;

        const orderWantedTime = wantedTime.time.slice(0, 19).replace(' ', 'T');

        const { notificationType, reason } = getNotificationType({
          canTrackOrderStatus,
          wantedTime: orderWantedTime,
        });

        track('notification_preferences.order_notification_type', {
          orderId,
          notificationType,
          notificationTypeReason: reason,
        });

        const deliveryParams = getDeliveryParams(orderChannel, selectedTip, deliveryOrderDetail); // prettier-ignore

        const result = await submitOrderWithPaymentMethod({
          dropoffLocationId,
          paymentMethodId: paymentMethodOrIntentId,
          orderId,
          pickupTip: selectedTip,
          notificationType,
          wantedTime: orderWantedTime,
          useCredit: shouldUseCredit,
          useHsa: shouldUseFlex,
          includeUtensils: shouldIncludeUtensils,
          deliveryOrderDetail: deliveryParams,
          shouldTrackPurchase: true,
        });

        const typename = result?.__typename;

        if (typename === 'SubmitOrderUsingPaymentMethodSuccess') {
          void setDefaultPaymentMethod(paymentMethodOrIntentId);

          return {
            order: result.order,
            success: true,
            isTimeslotUnavailable: false,
            error: undefined,
          };
        }

        track('checkout.failure', { systemError: typename });

        if (typename === 'TimeslotUnavailable') {
          return {
            success: false,
            isTimeslotUnavailable: true,
            error: 'TimeslotUnavailable',
          };
        }

        if (typename === 'MinimumDeliverySubtotalNotMet') {
          addNoticeBanner({
            palette: 'caution',
            text: formatMessage(messages.minimumDeliverySubtotalNotMet),
          });

          return {
            success: false,
            isTimeslotUnavailable: false,
            error: 'MinimumDeliverySubtotalNotMet',
          };
        }

        if (typename === 'ProductUnavailable') {
          addNoticeBanner({
            text: result.message,
            palette: 'caution',
          });

          return {
            success: false,
            isTimeslotUnavailable: false,
            error: 'ProductUnavailable',
          };
        }

        if (typename === 'StoreOffline') {
          addNoticeBanner({
            text: formatMessage(messages.storeOffline),
            palette: 'caution',
          });

          return {
            success: false,
            isTimeslotUnavailable: false,
            error: 'StoreOffline',
          };
        }

        addNoticeBanner({
          text: formatMessage(messages.orderSubmissionError, { typename }),
          palette: 'caution',
        });

        return {
          success: false,
          isTimeslotUnavailable: false,
          error: undefined,
        };
      }),
    [
      getNotificationType,
      track,
      submitOrderWithPaymentMethod,
      addNoticeBanner,
      formatMessage,
      setDefaultPaymentMethod,
    ],
  );

  // ──────────────────────────────────────────────────────────────────────────

  return { submitOrder };
};

// ─── Helpers ────────────────────────────────────────────────────────────────

const useSubmitOrderWithPaymentMethodMutation = () => {
  const client = useClient();

  return useCallback(
    async (input: SubmitBagOrderWithPaymentMethodMutationVariables['input']) => {
      const mutation = client.mutation<
        SubmitBagOrderWithPaymentMethodMutation,
        SubmitBagOrderWithPaymentMethodMutationVariables
      >;

      const response = await mutation(
        SubmitBagOrderWithPaymentMethodDocument,
        { input },
        { requestPolicy: 'network-only' },
      ).toPromise();

      return response.data?.submitOrderUsingPaymentMethod;
    },
    [client.mutation],
  );
};

// ─── Messages ───────────────────────────────────────────────────────────────

const messages = defineMessages({
  missingWantedTime: {
    defaultMessage: 'Please select a time for this order',
    description: 'Bag | Order submission error | Missing wanted time',
  },
  storeOffline: {
    defaultMessage:
      'This store is currently offline. Please try again later or choose a different store.',
    description: 'Bag | Order submission error | Store offline',
  },
  orderSubmissionError: {
    defaultMessage: `{typename, select,
    IncorrectPaymentMethod {The card information provided is invalid.
If the issue persists, please contact your card issuer for further assistance.}
    DeclinedPaymentMethod {We're sorry but this transaction has been declined.
Please contact your card issuer or choose an alternative payment method.}
    InvalidCustomerPhone {Your phone number is invalid. Please use a 10 digit US phone number, without country code.}
    InvalidTip {Selected tip cannot be applied. Please choose another option.}
    DeliveryAlreadyInFlight {Sorry, you already have an active delivery order. Please try Pickup, or check the status of your active delivery order.}
    DeliverySpecifyAddress {More than one result was returned for this address. Please make this address as specific as possible.}
    other {Something went wrong, please try again later.}
  }`,
    description: 'Bag | Order submission error',
  },
  minimumDeliverySubtotalNotMet: {
    defaultMessage: `You haven't met the minimum subtotal for delivery.`,
    description: 'Bag | Order submission error | Minimum delivery subtotal not met',
  },
  timeslotUnavailable: {
    defaultMessage:
      'The selected timeslot window is no longer available. Your order has been updated to the next available time.',
    description: 'Bag | Order submission error | Timeslot unavailable',
  },
});
